import { useDispatch } from 'react-redux';
import { Pencil } from 'lucide-react';
import { renameFile } from '../v2Upload.slice';
import { V2File } from '../v2Upload.d';

interface FileNameInputProps {
  file: V2File;
  /** Draggable parents must drop `draggable` while this is focused — a
   * draggable ancestor otherwise swallows text selection inside the input. */
  onEditingChange?: (editing: boolean) => void;
}

/** The basename, with the extension suffix stripped off when present. */
function stripSuffix(name: string, suffix: string): string {
  if (suffix && name.toLowerCase().endsWith(suffix.toLowerCase())) {
    return name.slice(0, -suffix.length);
  }
  return name;
}

/** Only the basename is editable. The extension is load-bearing — the backend
 * derives file type and G-code controller from it — so it renders as a fixed
 * suffix rather than free text. */
export default function FileNameInput({
  file,
  onEditingChange,
}: FileNameInputProps) {
  const dispatch = useDispatch();

  const suffix = file.ext ? `.${file.ext}` : '';
  const base = stripSuffix(file.name, suffix);

  const commit = (nextBase: string) => {
    dispatch(renameFile({ fileId: file.id, name: `${nextBase}${suffix}` }));
  };

  return (
    <div className="flex items-center gap-1 min-w-0 rounded-lg border border-border bg-muted px-2.5 py-1.5 transition-colors hover:border-border-strong focus-within:border-primary">
      <input
        type="text"
        aria-label="File name"
        value={base}
        onChange={(e) => commit(e.target.value)}
        onFocus={() => onEditingChange?.(true)}
        onBlur={(e) => {
          onEditingChange?.(false);
          if (!e.target.value.trim()) commit('untitled');
        }}
        title={file.name}
        className="min-w-0 flex-1 bg-transparent text-[13px] font-semibold text-foreground truncate focus:outline-none"
      />
      {suffix && (
        <span className="shrink-0 text-[13px] font-semibold text-faint">
          {suffix}
        </span>
      )}
      <Pencil className="w-3.5 h-3.5 shrink-0 text-faint" aria-hidden="true" />
    </div>
  );
}
